import * as Sentry from '@sentry/nuxt'
import pickBy from 'lodash/pickBy'
import urljoin from 'url-join'

import pkg from '../../package.json'

import { useRuntimeConfig } from '#app'
import { defineNuxtPlugin } from '#imports'
import { useAuthToken } from '~/composables/auth/useAuthToken.js'
import { getErr998SentryExtra, checkErr998SelfDedupe, getUrlEndpointPath, summarizeHostProbe } from '~/utils/err998SentryExtra.js'
import { isNetworkRequestError } from '~/utils/requestError.js'
import { claimErr998ReportSlot } from '~/utils/suppressErr998Report.js'

// 客戶端指數退避 + jitter：base 500ms，第1次重試 2^1*500=1000ms ~1000-1200ms、第2次 2^2*500=2000ms ~2000-2400ms（maxRetries=2，最多重試到此為止）
// （原本 retryDelay: 1000 為固定延遲，並非如舊註解所述「ofetch 會自動使用指數退避」——
// ofetch 的 retryDelay 為數字時就是固定值，不會遞增，已用 node_modules/ofetch 原始碼確認）
// SSR 的重試 sleep 會阻塞 render／拉長 TTFB，故 server 端維持固定 1000ms，不計算 jitter
// （兩次重試合計 ≤ 2s，與原本固定 retryDelay: 1000 的總延遲一致，不新增 SSR 阻塞）。
// 暫停 ERR_998 上報：在組裝與節流之前就中止，不建立事件也不佔用節流額度；恢復時改回 false 即可
const IS_ERR_998_REPORT_PAUSED = true
const RETRY_DELAY_CAP_MS = import.meta.server ? 1000 : 3000
function computeRetryDelay(attempt) {
  if (import.meta.server) return RETRY_DELAY_CAP_MS
  const base = 500 * 2 ** (attempt + 1)
  const jitter = base * 0.2 * Math.random()
  return Math.min(base + jitter, RETRY_DELAY_CAP_MS)
}

// 重試時優先切換到測線判定健康的其他 host，而非死守剛失敗的同一台
// （對應「host 測線皆健康但請求仍失敗」的瞬斷情境）；ofetch 內建 retry 沒有這個能力，
// 因為它固定用同一個已組好的 request URL 字串重打，故本檔案改用自訂迴圈接管。
function pickHealthyHost(attemptedHosts, hostTesterContext) {
  const results = hostTesterContext?.hostTester?.lastResults || []
  const healthy = results.filter((r) => r.status === 'success' && r.url && !attemptedHosts.has(r.url))
  if (healthy.length === 0) return null
  return healthy.reduce((fastest, current) => {
    const fastestSpeed = fastest.speed ?? Infinity
    const currentSpeed = current.speed ?? Infinity
    return currentSpeed < fastestSpeed ? current : fastest
  }).url
}

function isAbortLikeError(error, signal) {
  // 刻意不比對訊息中的 abort/cancel，避免誤吞業務錯誤；ofetch 的 TimeoutError 仍須走重試與錯誤處理。
  return Boolean(
    signal?.aborted ||
    error?.__isCancel ||
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.cause?.name === 'AbortError' ||
    error?.cause?.name === 'CanceledError'
  )
}

// 需要重試的 HTTP 狀態碼：同時供重試迴圈判斷、以及 plugin 內部的暫時性/契約異常分類共用
// 同一份清單，避免兩處各自維護導致自相矛盾（504 曾因此被誤判為 MalformedApiResponse，CLIENT-PWA-SSR-1TE）
const TRANSIENT_HTTP_STATUS_CODES = [408, 409, 425, 429, 500, 502, 503, 504]

function isRetryableFetchError(error) {
  // 業務層明確排除重試的錯誤碼（noRetryErrorCodes）與請求取消（AbortError）
  // 皆無 .status，須在狀態碼判斷前先排除，否則會被誤判為「網路層失敗、可重試」
  if (error?.noRetry === true) return false
  if (isAbortLikeError(error)) return false
  const status = error?.response?.status || error?.status
  if (status) return TRANSIENT_HTTP_STATUS_CODES.includes(status)
  // 抓不到 status 但已收到後端回應主體（如 ERR_A108 等業務錯誤），代表並非網路層失敗，
  // 不可預設當作可重試，否則會吃掉外層 catch 專屬的業務錯誤處理（如 A108 靜默重試 + token 閘門）
  if (error?.data) return false
  // body 讀取失敗會直接拋出 TypeError；只重試 ofetch 包裝且尚未收到 response 的錯誤，避免重送已完成的寫入。
  return error?.name === 'FetchError' && !error.response
}

export default defineNuxtPlugin((nuxtApp) => {
  const { apiUrl, clientId, intranet } = useRuntimeConfig().public
  const { getAuthHeaders } = useAuthToken()
  const requestContext = import.meta.server ? { isDisposed: false, pendingRetries: new Map() } : null

  let _errorHandler = null
  function setErrorHandler(handler) {
    _errorHandler = handler
  }

  // 清除 error handler，避免閉包持有舊的元件引用
  function clearErrorHandler() {
    _errorHandler = null
  }

  function isValid(value) {
    return value !== undefined && value !== null
  }

  // 送出前 token 閘門已於 request() 內恢復（bb76daa7e 曾移除、全交後端回 ERR_A103，導致無 token 請求
  // 照送並在後端產生大量 TOKEN_CHECK_FAIL）。現以後端回應形狀合成 ERR_A103 並餵入 _errorHandler，
  // 保留 TI-15375 的屏蔽/GA/靜默回大廳管線；後端 ERR_A103 仍為兜底（isPublic 漏標、SSR 等情境）。
  const noRetryErrorCodes = ['ERR_F108', 'ERR_G111', 'ERR_A224', 'ERR_P132']

  // 沿用檔案頂部的 TRANSIENT_HTTP_STATUS_CODES：同一份清單同時決定「重試迴圈要重試哪些狀態」
  // 與「失敗要歸類為暫時性還是契約異常」，避免兩處各自維護清單造成自相矛盾（見該常數註解）。
  const transientHttpStatusCodes = TRANSIENT_HTTP_STATUS_CODES

  // 請求攔截器列表
  const requestInterceptors = []

  // 註冊攔截器
  const addRequestInterceptor = (interceptor) => {
    requestInterceptors.push(interceptor)
    // 返回移除函數
    return () => {
      const index = requestInterceptors.indexOf(interceptor)
      if (index > -1) {
        requestInterceptors.splice(index, 1)
      }
    }
  }

  // 清除所有攔截器，避免閉包累積造成記憶體洩漏
  const clearAllInterceptors = () => {
    requestInterceptors.length = 0
  }

  // 取得當前攔截器數量（用於除錯）
  const getInterceptorCount = () => requestInterceptors.length

  const captureNetworkFailure = ({ error, endpoint, method, attempt, maxAttempts, reportSlots }) => {
    if (IS_ERR_998_REPORT_PAUSED) return
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return
    const errData998 = getErr998SentryExtra({
      endpoint,
      method,
      clientId,
      uidSite: nuxtApp._userInfoContext?.uidSite?.value,
      clientIp: nuxtApp.$clientIp?.value,
    })
    const fingerprint = ['ERR_998', errData998.method, errData998.apiHost, errData998.endpoint]
    const key = fingerprint.join('|')
    let slot = reportSlots.get(key)
    // 每個請求只申請一次各 host 的節流額度，重試不可互相抑制。
    if (!slot) {
      slot = { shouldReport: true, suppressedCount: 0 }
      if (errData998.endpointClass === 'self') {
        const dedupeKey = [errData998.site, errData998.apiHost, 'self', errData998.uidSite || 'anonymous'].join('|')
        const dedupe = checkErr998SelfDedupe(dedupeKey)
        slot = { shouldReport: dedupe.allow, suppressedCount: dedupe.suppressedCount }
      } else if (errData998.endpointClass !== 'login') {
        slot = claimErr998ReportSlot(key)
      }
      reportSlots.set(key, slot)
    }
    if (!slot.shouldReport) return
    const errorMessage = error.message?.replace(/\?[^\s"]*/g, '?[REDACTED]')
    const networkErrorCode = error.cause?.code
    const sentryError = new Error(`ERR_998: [${errData998.method}] ${errData998.apiHost}${errData998.endpoint} <no response>`)
    sentryError.name = 'ERR_998'
    // 測線結果同時決定 tag（可搜尋的可達性分類）與 extra（逐筆明細）：先淺拷貝定格，
    // 否則 lastResults 是 HostTester 持續改寫的同一個陣列，事件序列化前若又跑完一輪測線，
    // extra 會變成新一輪結果而與 tag 對不起來
    const hostTester = nuxtApp._hostTesterContext?.hostTester
    const hostTestResults = hostTester?.lastResults?.slice() || []
    const hostProbe = summarizeHostProbe(hostTestResults, hostTester?.hosts?.length)
    Sentry.captureException(sentryError, {
      level: 'warning',
      fingerprint,
      tags: {
        ...errData998,
        requestFailedToFetch: 'true',
        retryAttempt: attempt,
        retryMaxAttempts: maxAttempts,
        ...(networkErrorCode ? { networkErrorCode } : {}),
        hostReachability: hostProbe.reachability,
      },
      extra: {
        ...errData998,
        errorMessage,
        networkErrorCode: networkErrorCode || null,
        hostTestResults,
        hostProbeAgeMs: hostProbe.probeAgeMs,
        ...(slot.suppressedCount > 0 ? { suppressedCount: slot.suppressedCount } : {}),
      },
    })
    slot.suppressedCount = 0
  }

  const request = async (options) => {
    if (requestContext?.isDisposed) return { data: {} }
    let endpoint
    try {
      // 執行攔截器 - 允許修改 options 或進行其他處理
      for (const interceptor of requestInterceptors) {
        // 如果攔截器返回 false，則取消請求
        const result = interceptor(options)
        if (result === false) {
          const cancelError = new Error('Canceled')
          cancelError.name = 'CanceledError'
          cancelError.__isCancel = true
          throw cancelError
        }
      }

      const { url, method, params, isPublic, headers, isFormData = false, signal } = options
      const isIntranetServer = import.meta.server && intranet === '1'
      let currentHost = isIntranetServer ? 'http://frontend-api-v2.default.svc.cluster.local' : nuxtApp.$getFastApiUrl?.() || apiUrl
      const attemptedHosts = new Set([currentHost])
      endpoint = urljoin(currentHost, url.replace(':clientId', clientId))

      // 原子化快照：閘門判斷與實際送出使用同一次 header 讀取，
      // 避免 cookie ref 在守衛檢查與 $fetch 之間被 CookieStore 清空（TOCTOU）
      const authHeaders = getAuthHeaders()
      if (!isPublic && !authHeaders.Authorization) {
        const blockedError = {
          code: 'ERR_A103',
          name: 'TOKEN_NEEDED',
          message: 'No token provided',
          http_url: endpoint,
          http_method: method.toUpperCase(),
        }
        _errorHandler && _errorHandler(blockedError)
        const gateError = new Error(blockedError.message)
        Object.assign(gateError, blockedError)
        gateError.url = endpoint
        throw gateError
      }

      const isGet = method.toLowerCase() === 'get'
      const payloadKey = isGet ? 'query' : 'body'
      const payload = isFormData ? params : pickBy(params, isValid)
      const maxRetries = 2
      // 帶 Authorization 的請求不得被瀏覽器 HTTP 快取：API 回應沒有 Cache-Control / Vary: Authorization，
      // 快取命中時可能把前一個身分的回應交給目前的請求（spec 2026-10-19 REQ-1）。
      // 以「有沒有帶 token」而非 isPublic 判斷：isPublic 的端點（如 feeds）登入時也會帶 token、回應可能因人而異。
      // SSR 不受瀏覽器快取影響，只加在 client
      const cacheOptions = import.meta.client && authHeaders.Authorization ? { cache: 'no-store' } : {}

      const reportSlots = new Map()
      let res
      let attempt = 0
      while (true) {
        if (requestContext?.isDisposed) return { data: {} }
        try {
          res = await $fetch(endpoint, {
            method,
            headers: {
              'X-TH1GAMES-VERSION': `v${pkg.version}`,
              'X-TH1GAMES-PLATFORM': 'pwa',
              ...authHeaders,
              ...headers,
            },
            [payloadKey]: payload,
            ...cacheOptions,
            retry: false, // 改由外層迴圈接管：ofetch 內建 retry 固定重打同一個 request 字串，無法切換 host
            signal, // 傳遞 signal 支援 AbortController
            onResponseError({ response }) {
              const code = response?._data?.error?.code
              if (code && noRetryErrorCodes.includes(code)) {
                const noRetryError = new Error(response?._data?.error?.message || `No-retry error: ${code}`)
                noRetryError.data = response?._data
                noRetryError.noRetry = true
                // 保留 response 供 catch block 的 e.response?.status 取值，否則 no-retry 錯誤碼
                // （ERR_F108/ERR_G111/ERR_A224/ERR_P132）的 error_event 會漏帶真實 http_status，
                // 退回 fallback 值（發現於 JT-1777948860 動態驗證：登入失敗 ERR_A224 的 http_status 恆為 0）。
                noRetryError.response = response
                throw noRetryError
              }
            },
          })
          if (requestContext?.isDisposed) return { data: {} }
          break
        } catch (fetchError) {
          if (requestContext?.isDisposed) return { data: {} }
          if (isAbortLikeError(fetchError, signal)) throw fetchError
          if (isNetworkRequestError(fetchError)) {
            captureNetworkFailure({
              error: fetchError,
              endpoint,
              method,
              attempt: attempt + 1,
              maxAttempts: maxRetries + 1,
              reportSlots,
            })
          }
          const canRetry = attempt < maxRetries && isRetryableFetchError(fetchError)
          if (!canRetry) throw fetchError
          await new Promise((resolve, reject) => {
            if (signal?.aborted) {
              reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
              return
            }
            const handleAbort = () => {
              clearTimeout(timer)
              signal?.removeEventListener('abort', handleAbort)
              requestContext?.pendingRetries.delete(timer)
              reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'))
            }
            const timer = setTimeout(() => {
              signal?.removeEventListener('abort', handleAbort)
              requestContext?.pendingRetries.delete(timer)
              resolve()
            }, computeRetryDelay(attempt))
            requestContext?.pendingRetries.set(timer, handleAbort)
            signal?.addEventListener('abort', handleAbort, { once: true })
          })
          if (requestContext?.isDisposed) return { data: {} }
          // intranet SSR 固定打叢集內網單一 host，沒有測線資料可切換，僅重試同一 host
          if (!isIntranetServer) {
            const nextHost = pickHealthyHost(attemptedHosts, nuxtApp._hostTesterContext)
            if (nextHost) {
              attemptedHosts.add(nextHost)
              currentHost = nextHost
              endpoint = urljoin(currentHost, url.replace(':clientId', clientId))
            }
          }
          attempt += 1
        }
      }

      // 204/205/304 等無 body 回應 ofetch 會 resolve undefined（瀏覽器偶爾把同端點並發請求的 304 直接交給 JS），
      // 屬合法的成功回應而非契約錯誤：原樣交還呼叫端依各自契約處理，不在此炸成 TypeError（CLIENT-PWA-SSR-1R6）
      if (res?.data?.error) {
        const apiError = new Error(res.data.error.message || res.data.error.code || 'API Error')
        Object.assign(apiError, res.data.error)
        // 標記為後端業務錯誤（2xx body 帶 error payload，非 JS 回歸）：供 fire-and-forget 呼叫端辨識，
        // 避免已由 store fail 狀態呈現的業務錯誤再變成未捕捉 rejection 重複上報（CLIENT-PWA-SSR-16S）。
        // 本分支由 `if (res.data?.error)` 守門，error payload 必存在。
        apiError.isBusinessError = true
        throw apiError
      }
      return res
    } catch (e) {
      if (requestContext?.isDisposed) return { data: {} }
      // 忽略各種形式的請求取消錯誤
      const isAbortError = isAbortLikeError(e, options?.signal)

      if (isAbortError) {
        console.log('[Request] Canceled or Aborted')
        return { data: {} }
      }

      if (isNetworkRequestError(e)) throw e
      if (e.data) {
        // ERR_A108 token 過期：交付錯誤處理器（屏蔽/登出）前先靜默重試一次，
        // 涵蓋 extendToken 輪替 token 的短暫競態，降低真正屏蔽的次數。
        if (e.data.error?.code === 'ERR_A108' && !options._a108Retried) {
          return await request({ ...options, _a108Retried: true })
        }
        // 標記 isBusinessError 供 fire-and-forget 呼叫端安全吞掉：此類已交付 _errorHandler 呈現（CLIENT-PWA-SSR-16S）
        if (e.data.error) {
          e.data.error.http_url = endpoint
          e.data.error.http_method = options.method?.toUpperCase()
          e.data.error.http_status = e.statusCode ?? e.status ?? e.response?.status ?? null
          _errorHandler && _errorHandler(e.data.error)
          const dataError = new Error(e.data.error.message || e.data.error.code || 'Request Error')
          Object.assign(dataError, e.data.error)
          dataError.isBusinessError = true
          throw dataError
        }
        // 契約異常回應刻意不標記 isBusinessError 且照常 throw，以利上報、不遮蔽問題（CLIENT-PWA-SSR-16S）；
        // 端點去 query 避免 appSecret 進 Sentry 標題，去版本號與 clientId 避免同一支 API 依站台拆成多個 issue
        const responseStatus = e.statusCode ?? e.status ?? e.response?.status ?? null
        const requestMethod = options?.method ?? ''
        const endpointPath = getUrlEndpointPath(endpoint)
        // 上游沒能產生有效回應（閘道 5xx / 逾時 / 限流），與 Failed to fetch 屬同一類暫時性失敗，
        // 而非後端破壞了錯誤契約：比照 ERR_998 以 warning 上報，並標記供 fire-and-forget 呼叫端
        // 安全吞掉，不再升級成 handled:no 的未捕捉 rejection（CLIENT-PWA-SSR-1TE）
        const isTransientUpstream = transientHttpStatusCodes.includes(responseStatus)
        const failureName = isTransientUpstream ? 'TransientUpstreamError' : 'MalformedApiResponse'
        const malformedError = new Error(`[${requestMethod}] ${endpointPath} <${responseStatus ?? 'no status'}>`)
        malformedError.name = failureName
        malformedError.status = responseStatus
        if (isTransientUpstream) {
          // 供 fire-and-forget 呼叫端辨識；同時是 sentry.client.config.ts 丟棄「全域 handler 二次捕獲」的
          // 依據——未以 catch 承接的呼叫端（如 components/modal/Merge.vue 的 $request.send()）會讓這個
          // 錯誤傳到 unhandledrejection，與下方的結構化上報重複（CLIENT-PWA-SSR-1TE）
          malformedError.isTransientUpstreamError = true
          // 上游故障時每個端點、每個使用者都會同時失敗，沿用 ERR_998 的 60 秒單鍵節流避免灌爆；
          // 被擋下的次數隨下一筆帶出，降噪後仍看得出故障規模
          const fingerprint = [failureName, requestMethod, endpointPath, String(responseStatus)]
          const { shouldReport, suppressedCount } = claimErr998ReportSlot(fingerprint.join('|'))
          if (shouldReport) {
            // 比照 ERR_998 另建上報用 Error：必須與被 throw 的那一個是不同物件且不帶上述旗標，
            // 否則這筆監控訊號會被自己的丟棄規則一併濾掉
            const sentryError = new Error(`${failureName}: [${requestMethod}] ${endpointPath} <${responseStatus}>`)
            sentryError.name = failureName
            Sentry.captureException(sentryError, {
              level: 'warning',
              fingerprint,
              tags: { method: requestMethod, endpoint: endpointPath, httpStatus: String(responseStatus) },
              ...(suppressedCount > 0 ? { extra: { suppressedCount } } : {}),
            })
          }
        }
        throw malformedError
      }
      throw e
    }
  }

  if (import.meta.server) {
    const requestNode = nuxtApp.ssrContext?.event?.node?.req
    const responseNode = nuxtApp.ssrContext?.event?.node?.res
    const cleanup = () => {
      if (requestContext.isDisposed) return
      requestContext.isDisposed = true
      // 取消時也 reject 等待，避免只清 timer 使 request Promise 永遠 pending。
      requestContext.pendingRetries.forEach((cancel) => cancel())
      requestContext.pendingRetries.clear()
      requestNode?.removeListener('aborted', cleanup)
      responseNode?.removeListener('close', cleanup)
      clearAllInterceptors()
      clearErrorHandler()
    }
    requestContext.cleanup = cleanup
    nuxtApp._requestContext = requestContext
    nuxtApp.hooks.hook('app:rendered', cleanup)
    nuxtApp.hooks.hook('app:error', cleanup)
    requestNode?.once('aborted', cleanup)
    responseNode?.once('close', cleanup)
    if (requestNode?.aborted || responseNode?.destroyed) cleanup()
  }

  return {
    provide: {
      request: {
        send: request,
        setErrorHandler,
        addRequestInterceptor,
        clearErrorHandler,
        clearAllInterceptors,
        getInterceptorCount,
      },
    },
  }
})
